import React, { memo, useMemo } from 'react';
import { Text, TextProps } from '@chakra-ui/react';
import { getRelativeTimeString } from '@utils/dateUtils';

interface RelativeTimeProps extends TextProps {
  date: string | Date;
  fallbackDate?: string | Date;
}

const RelativeTime = memo<RelativeTimeProps>(({ 
  date, 
  fallbackDate, 
  ...textProps 
}) => {
  // Only recalculate date object when input strings change
  const targetDateVar = date || fallbackDate || new Date();
  
  // Memoize the Date object itself to prevent effect/memo re-runs if string is stable
  const targetDate = useMemo(() => 
    new Date(targetDateVar), 
    [targetDateVar]
  );

  // Calculate time string - this is now fast thanks to singleton formatter
  // We explicitly depend on targetDate.getTime() to avoid object reference issues
  const timeAgo = useMemo(() => 
    getRelativeTimeString(targetDate), 
    [targetDate.getTime()]
  );
  
  const fullDateTime = useMemo(() => 
    targetDate.toLocaleString(), 
    [targetDate.getTime()]
  );

  return (
    <Text 
      fontSize="sm" 
      color="gray.600" 
      _dark={{ color: "gray.300" }}
      title={fullDateTime}
      {...textProps}
    >
      {timeAgo}
    </Text>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to ensure we don't re-render if date string/object hasn't changed value
  // This is critical for table performance
  const prevDate = prevProps.date instanceof Date ? prevProps.date.getTime() : prevProps.date;
  const nextDate = nextProps.date instanceof Date ? nextProps.date.getTime() : nextProps.date;
  const prevKeys = Object.keys(prevProps);
  const nextKeys = Object.keys(nextProps);
  
  return prevDate === nextDate && 
         prevProps.fallbackDate === nextProps.fallbackDate &&
         prevKeys.length === nextKeys.length &&
         // Shallow compare rest of props (style related usually)
         prevKeys.every(key => 
           key === 'date' || key === 'fallbackDate' || 
           prevProps[key as keyof RelativeTimeProps] === nextProps[key as keyof RelativeTimeProps]
         );
});

RelativeTime.displayName = 'RelativeTime';

export default RelativeTime; 
